import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Schedule,
  Person,
  Phone,
  Badge,
  CreditCard,
  Refresh,
  DeleteOutline
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { handleApiError } from '../utils/api';
import { isWireTransferEnabled } from '../utils/featureFlags';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminVerificationPage = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [pendingApplications, setPendingApplications] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingClassPayments, setPendingClassPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // UI states
  const [activeTab, setActiveTab] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve', 'reject', 'view', 'delete'
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Admin notes modal states
  const [adminNotesModalOpen, setAdminNotesModalOpen] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [adminNotesInput, setAdminNotesInput] = useState('');
  
  // Filters
  const [userTypeFilter, setUserTypeFilter] = useState('');


  const loadPendingApplications = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userTypeFilter) params.append('userType', userTypeFilter);

      const response = await axios.get(`${API_URL}/admin/verification/pending?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPendingApplications(response.data.data || []);
      } else {
        setPendingApplications([]);
      }
    } catch (error) {
      console.error('Error loading pending applications:', error);
      setPendingApplications([]);
      throw error;
    }
  }, [userTypeFilter, token]);

  const loadPendingPayments = useCallback(async () => {
    try {
      // Load both old pending payments and new payment tokens
      const [oldPaymentsResponse, allPaymentsResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/verification/pending-payment`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/verification/all-pending-payments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const oldPayments = oldPaymentsResponse.data.success ? oldPaymentsResponse.data.data || [] : [];
      const newPayments = allPaymentsResponse.data.success ? allPaymentsResponse.data.data || [] : [];

      // Combine payments and FILTER OUT class payments (CLS- tokens)
      const allPayments = [...oldPayments, ...newPayments];
      const registrationPaymentsOnly = allPayments.filter(payment =>
        !payment.paymentToken?.startsWith('CLS-')
      );

      setPendingPayments(registrationPaymentsOnly);
    } catch (error) {
      console.error('Error loading pending payments:', error);
      setPendingPayments([]);
      throw error;
    }
  }, [token]);

  const loadPendingClassPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/verification/pending-class-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPendingClassPayments(response.data.data || []);
      } else {
        setPendingClassPayments([]);
      }
    } catch (error) {
      console.error('Error loading pending class payments:', error);
      setPendingClassPayments([]);
      throw error;
    }
  }, [token]);

  const loadStatistics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/verification/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatistics(response.data.data);
      } else {
        setStatistics(null);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStatistics(null);
      // Don't throw for statistics as it's not critical
    }
  }, [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Load data independently to avoid one failure blocking others
      const results = await Promise.allSettled([
        loadPendingApplications(),
        loadPendingPayments(),
        loadPendingClassPayments(),
        loadStatistics()
      ]);

      // Check for any rejections
      const failedLoads = results.filter(result => result.status === 'rejected');
      if (failedLoads.length > 0) {
        console.warn('Some data failed to load:', failedLoads);
        setError('Some data failed to load, but page is functional');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load verification data');
    } finally {
      setLoading(false);
    }
  }, [loadPendingApplications, loadPendingPayments, loadPendingClassPayments, loadStatistics]);

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, loadData]);

  const handleViewApplication = async (applicationId) => {
    setActionLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/verification/application/${applicationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSelectedApplication(response.data.data);
        setActionType('view');
        setDialogOpen(true);
      }
    } catch (error) {
      console.error('Error loading application details:', error);
      setError('Failed to load application details');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = (application) => {
    setSelectedApplication(application);
    setActionType('approve');
    setAdminNotes('');
    setDialogOpen(true);
  };

  const handleReject = (application) => {
    setSelectedApplication(application);
    setActionType('reject');
    setRejectionReason('');
    setAdminNotes('');
    setDialogOpen(true);
  };

  const handleDelete = (application) => {
    setSelectedApplication(application);
    setActionType('delete');
    setAdminNotes('');
    setDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (actionType === 'view') {
      setDialogOpen(false);
      return;
    }

    if (actionType === 'reject' && !rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    setActionLoading(true);
    setError('');

    try {
      let response;
      
      if (actionType === 'delete') {
        // Delete pending user
        response = await axios.delete(
          `${API_URL}/users/${selectedApplication.user._id}/pending`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Approve or reject
        const endpoint = actionType === 'approve' ? 'approve' : 'reject';
        const payload = actionType === 'approve' 
          ? { adminNotes } 
          : { rejectionReason, adminNotes };

        response = await axios.post(
          `${API_URL}/admin/verification/${endpoint}/${selectedApplication.applicationId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data.success) {
        setSuccess(response.data.message);
        setDialogOpen(false);
        setSelectedApplication(null);
        setAdminNotes('');
        setRejectionReason('');
        await loadData(); // Refresh data
      }
    } catch (error) {
      console.error(`Error ${actionType}ing application:`, error);
      setError(handleApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmPayment = (applicationId, amount, planName) => {
    // Open admin notes modal for payment confirmation
    setPendingPaymentData({
      type: 'payment',
      applicationId,
      amount,
      planName
    });
    setAdminNotesInput('');
    setAdminNotesModalOpen(true);
  };

  const confirmPaymentWithNotes = async () => {
    if (!pendingPaymentData) return;

    const paymentMethod = 'cash';
    const paymentReference = '';
    const adminNotes = adminNotesInput.trim();

    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/verification/confirm-payment/${pendingPaymentData.applicationId}`,
        {
          paymentMethod,
          paymentReference,
          amount: pendingPaymentData.amount,
          adminNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Payment confirmed successfully');
        await loadData();
        setAdminNotesModalOpen(false);
        setPendingPaymentData(null);
        setAdminNotesInput('');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      setError(handleApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmInPlacePayment = async (paymentToken, userName) => {
    // Directly confirm payment without prompts
    const paymentMethod = 'card';
    const receiptNumber = '';
    const notes = '';

    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/verification/confirm-in-place-payment`,
        {
          paymentToken,
          receiptNumber,
          paymentMethod: paymentMethod.toLowerCase(),
          notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('In-place payment confirmed successfully');
        await loadData();
      }
    } catch (error) {
      console.error('Error confirming in-place payment:', error);
      setError(handleApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmWireTransfer = async (paymentToken, userName) => {
    // Directly confirm transfer without prompts
    const transferReceiptUrl = '';
    const notes = '';

    setActionLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/verification/confirm-wire-transfer`,
        {
          paymentToken,
          transferReceiptUrl: transferReceiptUrl || undefined,
          notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Wire transfer confirmed successfully');
        await loadData();
      }
    } catch (error) {
      console.error('Error confirming wire transfer:', error);
      setError(handleApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmClassPayment = async (paymentToken, _userName, paymentMethod) => {
    // Directly confirm class payment without prompts
    const notes = '';
    let additionalInfo = {};

    setActionLoading(true);
    try {
      // Use the correct endpoint based on payment method - same as registration payments
      let endpoint, payload;
      
      if (paymentMethod === 'wire_transfer') {
        endpoint = `${API_URL}/admin/verification/confirm-wire-transfer`;
        payload = {
          paymentToken,
          transferReceiptUrl: additionalInfo.transferReceiptUrl,
          notes
        };
      } else if (paymentMethod === 'in_place') {
        endpoint = `${API_URL}/admin/verification/confirm-in-place-payment`;
        payload = {
          paymentToken,
          receiptNumber: additionalInfo.receiptNumber,
          paymentMethod: 'cash', // Default to cash for in-place
          notes
        };
      } else {
        throw new Error('Unsupported payment method for class payments');
      }

      const response = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Class payment confirmed successfully');
        await loadData();
      }
    } catch (error) {
      console.error('Error confirming class payment:', error);
      setError(handleApiError(error));
    } finally {
      setActionLoading(false);
    }
  };

  const formatUserType = (userType) => {
    const types = {
      student: t('admin:verification.filters.student'),
      technopolis_employee: t('admin:verification.filters.employee'), 
      external: t('admin:verification.filters.external')
    };
    return types[userType] || userType;
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      student: 'primary',
      technopolis_employee: 'secondary',
      external: 'default'
    };
    return colors[userType] || 'default';
  };

  const renderApplicationDetails = () => {
    if (!selectedApplication) return null;

    const { application, user: appUser } = selectedApplication;

    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('admin:verification.dialogs.personalInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.name')}:</Typography>
                <Typography variant="body1">{appUser.firstName} {appUser.lastName}</Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.email')}:</Typography>
                <Typography variant="body1">{appUser.email}</Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.userType')}:</Typography>
                <Chip 
                  label={formatUserType(appUser.userType)} 
                  color={getUserTypeColor(appUser.userType)}
                  size="small"
                />
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.tckn')}:</Typography>
                <Typography variant="body1">{appUser.tckn}</Typography>
              </Box>

              {appUser.universityProfile && (
                <>
                  {appUser.universityProfile.department && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.department')}:</Typography>
                      <Typography variant="body1">{appUser.universityProfile.department}</Typography>
                    </Box>
                  )}
                  {appUser.universityProfile.studentNumber && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.studentNumber')}:</Typography>
                      <Typography variant="body1">{appUser.universityProfile.studentNumber}</Typography>
                    </Box>
                  )}
                  {appUser.universityProfile.employeeId && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.employeeId')}:</Typography>
                      <Typography variant="body1">{appUser.universityProfile.employeeId}</Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('admin:verification.dialogs.emergencyContact')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.name')}:</Typography>
                <Typography variant="body1">{appUser.emergencyContact.name}</Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.relationship')}:</Typography>
                <Typography variant="body1">{appUser.emergencyContact.relationship}</Typography>
              </Box>
              
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.phone')}:</Typography>
                <Typography variant="body1">{appUser.emergencyContact.phone}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('admin:verification.dialogs.selectedPlan')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.planName')}:</Typography>
                    <Typography variant="body1">{application.selectedPlan?.name}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.amount')}:</Typography>
                    <Typography variant="body1">₺{application.estimatedAmount}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.applicationId')}:</Typography>
                    <Typography variant="body1" fontFamily="monospace">{application.applicationId}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">{t('admin:verification.fields.submittedAt')}:</Typography>
                    <Typography variant="body1">
                      {new Date(application.submittedAt || application.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderPendingApplicationsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          {t('admin:verification.tabs.pendingVerification', { count: pendingApplications.length })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>{t('admin:verification.filters.userType')}</InputLabel>
            <Select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              label={t('admin:verification.filters.userType')}
            >
              <MenuItem value="">{t('admin:verification.filters.allTypes')}</MenuItem>
              <MenuItem value="student">{t('admin:verification.filters.student')}</MenuItem>
              <MenuItem value="technopolis_employee">{t('admin:verification.filters.employee')}</MenuItem>
              <MenuItem value="external">{t('admin:verification.filters.external')}</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={t('admin:verification.filters.refresh')}>
            <IconButton onClick={loadData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {pendingApplications.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" data-testid="no-pending-applications">
            {t('admin:verification.empty.noPendingApplications')}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="all-processed-message">
            {t('admin:verification.empty.allProcessed')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} data-testid="pending-applications-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin:verification.table.headers.applicationId')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.name')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.email')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.userType')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.plan')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.amount')}</TableCell>
                <TableCell>{t('admin:verification.table.headers.waitingDays')}</TableCell>
                <TableCell align="center">{t('admin:verification.table.headers.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingApplications.map((app) => (
                <TableRow key={app.applicationId} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {app.applicationId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {app.user.firstName} {app.user.lastName}
                  </TableCell>
                  <TableCell>{app.user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={formatUserType(app.user.userType)} 
                      color={getUserTypeColor(app.user.userType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{app.selectedPlan?.name}</TableCell>
                  <TableCell>₺{app.estimatedAmount}</TableCell>
                  <TableCell>
                    <Chip 
                      label={t('admin:verification.table.days', { count: app.waitingDays })}
                      color={app.waitingDays > 2 ? 'error' : app.waitingDays > 1 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('admin:verification.actions.viewDetails')}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewApplication(app.applicationId)}
                          disabled={actionLoading}
                          data-testid="view-button"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:verification.actions.approve')}>
                        <IconButton 
                          size="small" 
                          color="success"
                          onClick={() => handleApprove(app)}
                          disabled={actionLoading}
                          data-testid="approve-button"
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:verification.actions.reject')}>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleReject(app)}
                          disabled={actionLoading}
                          data-testid="reject-button"
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('admin:verification.actions.delete')}>
                        <IconButton 
                          size="small" 
                          color="warning"
                          onClick={() => handleDelete(app)}
                          disabled={actionLoading}
                          data-testid="delete-button"
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderPendingPaymentsTab = () => (
    <Box>
      {/* Registration Payments Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {t('admin:verification.payments.registrationPayments', { count: pendingPayments.length })}
        </Typography>

        {pendingPayments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <CreditCard sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('admin:verification.empty.noPendingRegistrationPayments')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Application ID / Token</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingPayments.map((payment) => (
                  <TableRow key={payment.applicationId || payment.paymentToken} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {payment.applicationId || payment.paymentToken}
                      </Typography>
                      {payment.referenceCode && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ref: {payment.referenceCode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {payment.user ? 
                        `${payment.user.firstName || payment.user.name} ${payment.user.lastName || ''}`.trim() : 
                        payment.user?.name || 'Unknown'
                      }
                    </TableCell>
                    <TableCell>{payment.user?.email}</TableCell>
                    <TableCell>
                      {payment.selectedPlan?.name || payment.registration?.planName}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.method ? payment.method.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                        color={
                          payment.method === 'in_place' ? 'success' : 
                          payment.method === 'wire_transfer' ? 'info' : 
                          'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>₺{payment.amount}</TableCell>
                    <TableCell>
                      {payment.paymentDueDate ? 
                        new Date(payment.paymentDueDate).toLocaleDateString() : 
                        payment.expiresAt ? 
                          new Date(payment.expiresAt).toLocaleDateString() : 
                          'N/A'
                      }
                    </TableCell>
                    <TableCell align="center">
                      {payment.method === 'in_place' ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleConfirmInPlacePayment(payment.paymentToken, payment.user?.name)}
                          disabled={actionLoading}
                        >
                          Confirm In-Place
                        </Button>
                      ) : isWireTransferEnabled() && payment.method === 'wire_transfer' ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="info"
                          onClick={() => handleConfirmWireTransfer(payment.paymentToken, payment.user?.name)}
                          disabled={actionLoading}
                        >
                          Confirm Wire Transfer
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => handleConfirmPayment(payment.applicationId, payment.amount, payment.selectedPlan?.name)}
                          disabled={actionLoading}
                        >
                          Confirm Payment
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Class Payments Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {t('admin:verification.payments.classPayments', { count: pendingClassPayments.length })}
        </Typography>

        {pendingClassPayments.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <CreditCard sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t('admin:verification.empty.noPendingClassPayments')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Payment Token</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingClassPayments.map((payment) => (
                  <TableRow key={payment.paymentToken} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {payment.paymentToken}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.user ? 
                        `${payment.user.firstName} ${payment.user.lastName}`.trim() : 
                        'Unknown'
                      }
                    </TableCell>
                    <TableCell>{payment.user?.email}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {payment.scheduledClass?.classType?.name || 'Unknown Class'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.scheduledClass?.startTime 
                            ? new Date(payment.scheduledClass.startTime).toLocaleDateString() + ' at ' +
                              new Date(payment.scheduledClass.startTime).toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                            : 'No schedule'
                          }
                        </Typography>
                        {payment.scheduledClass?.facility && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {payment.scheduledClass.facility.name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={payment.paymentMethod ? payment.paymentMethod.replace('_', ' ').toUpperCase() : 'UNKNOWN'}
                        color={
                          payment.paymentMethod === 'in_place' ? 'success' : 
                          payment.paymentMethod === 'wire_transfer' ? 'info' : 
                          'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>₺{payment.amount}</TableCell>
                    <TableCell>
                      <Typography variant="body2" 
                        color={new Date(payment.expiresAt) < new Date() ? 'error' : 'text.primary'}>
                        {new Date(payment.expiresAt).toLocaleDateString()}
                      </Typography>
                      {new Date(payment.expiresAt) < new Date() && (
                        <Chip label="EXPIRED" color="error" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {payment.paymentMethod === 'credit_card' ? (
                        <Chip 
                          label="CREDIT CARD - AUTO PROCESS" 
                          color="default" 
                          size="small" 
                        />
                      ) : payment.paymentMethod === 'in_place' || (isWireTransferEnabled() && payment.paymentMethod === 'wire_transfer') ? (
                        <Button
                          size="small"
                          variant="contained"
                          color={payment.paymentMethod === 'in_place' ? 'success' : 'info'}
                          onClick={() => handleConfirmClassPayment(
                            payment.paymentToken,
                            `${payment.user?.firstName} ${payment.user?.lastName}`.trim(),
                            payment.paymentMethod
                          )}
                          disabled={actionLoading}
                        >
                          {payment.paymentMethod === 'in_place' ? 'Confirm In-Place' : 'Confirm Wire Transfer'}
                        </Button>
                      ) : (
                        <Chip
                          label={t('admin:verification.payments.paymentMethodDisabled')}
                          color="default"
                          size="small"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );


  const renderStatisticsTab = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('admin:verification.statistics.title')}
      </Typography>

      {statistics && (
        <Grid container spacing={3}>
          <Grid size={{xs:12 , sm:6 , md:3}}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" color="warning.main">
                  {statistics.overall.pendingVerification}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:verification.statistics.pendingVerification')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{xs:12 , sm:6 , md:3}}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CreditCard sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" color="info.main">
                  {statistics.overall.pendingPayment}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('admin:verification.statistics.pendingPayment')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {statistics.overall.processingTimes && (
            <>
              <Grid size={{xs:12 , sm:6 , md:3}}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" color="success.main">
                      {Math.round(statistics.overall.processingTimes.avgProcessingDays || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('admin:verification.statistics.avgProcessingDays')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Box>
  );

  if (!user || (user.role !== 'admin' && user.role !== 'receptionist')) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {t('admin:access.adminRequired')}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('admin:common.loading')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('admin:verification.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('admin:verification.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={t('admin:verification.tabs.pendingVerification', { count: pendingApplications.length })}
            icon={<Schedule />}
          />
          <Tab 
            label={t('admin:verification.tabs.pendingPayments', { count: pendingPayments.length + pendingClassPayments.length })}
            icon={<CreditCard />}
          />
          <Tab 
            label={t('admin:verification.tabs.statistics')}
            icon={<Badge />}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderPendingApplicationsTab()}
          {activeTab === 1 && renderPendingPaymentsTab()}
          {activeTab === 2 && renderStatisticsTab()}
        </Box>
      </Paper>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth data-testid="admin-action-dialog">
        <DialogTitle>
          {actionType === 'view' && t('admin:verification.dialogs.applicationDetails')}
          {actionType === 'approve' && t('admin:verification.dialogs.approveApplication')}
          {actionType === 'reject' && t('admin:verification.dialogs.rejectApplication')}
          {actionType === 'delete' && t('admin:verification.dialogs.deletePendingRegistration')}
        </DialogTitle>
        <DialogContent>
          {actionType === 'view' && renderApplicationDetails()}
          
          {actionType === 'approve' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('admin:verification.dialogs.approveConfirm', { 
                  name: `${selectedApplication?.user?.firstName} ${selectedApplication?.user?.lastName}` 
                })}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('admin:verification.dialogs.adminNotes')}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={t('admin:verification.dialogs.approveConfirm')}
                data-testid="admin-notes-input"
              />
            </Box>
          )}
          
          {actionType === 'reject' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('admin:verification.dialogs.rejectConfirm', { 
                  name: `${selectedApplication?.user?.firstName} ${selectedApplication?.user?.lastName}` 
                })}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('admin:verification.dialogs.rejectionReason')}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('admin:verification.dialogs.rejectionReason')}
                required
                data-testid="rejection-reason-input"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('admin:verification.dialogs.adminNotes')}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={t('admin:verification.dialogs.adminNotes')}
                data-testid="admin-notes-input"
              />
            </Box>
          )}
          
          {actionType === 'delete' && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('admin:verification.dialogs.deleteConfirm', { 
                  name: `${selectedApplication?.user?.firstName} ${selectedApplication?.user?.lastName}` 
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('admin:verification.dialogs.deleteWarning')}
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                {t('admin:verification.dialogs.deleteCannotUndo')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} data-testid="cancel-button">
            {t('admin:verification.buttons.cancel')}
          </Button>
          {actionType === 'approve' && (
            <Button 
              onClick={handleSubmitAction}
              variant="contained"
              color="success"
              disabled={actionLoading}
              data-testid="confirm-approve-button"
              startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle />}
            >
              {actionLoading ? t('admin:verification.buttons.approving') : t('admin:verification.buttons.approveApplication')}
            </Button>
          )}
          {actionType === 'reject' && (
            <Button 
              onClick={handleSubmitAction}
              variant="contained"
              color="error"
              disabled={actionLoading || !rejectionReason.trim()}
              data-testid="confirm-reject-button"
              startIcon={actionLoading ? <CircularProgress size={16} /> : <Cancel />}
            >
              {actionLoading ? t('admin:verification.buttons.rejecting') : t('admin:verification.buttons.rejectApplication')}
            </Button>
          )}
          {actionType === 'delete' && (
            <Button 
              onClick={handleSubmitAction}
              variant="contained"
              color="warning"
              disabled={actionLoading}
              data-testid="confirm-delete-button"
              startIcon={actionLoading ? <CircularProgress size={16} /> : <DeleteOutline />}
            >
              {actionLoading ? t('admin:verification.buttons.deleting') : t('admin:verification.buttons.deleteRegistration')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Admin Notes Modal */}
      <Dialog
        open={adminNotesModalOpen}
        onClose={() => setAdminNotesModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Payment - Admin Notes
        </DialogTitle>
        <DialogContent>
          {pendingPaymentData && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Plan:</strong> {pendingPaymentData.planName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Amount:</strong> ₺{pendingPaymentData.amount}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Admin Notes (Optional)"
            multiline
            rows={4}
            value={adminNotesInput}
            onChange={(e) => setAdminNotesInput(e.target.value)}
            placeholder="Add any additional notes about this payment confirmation..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAdminNotesModalOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmPaymentWithNotes}
            variant="contained"
            color="success"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {actionLoading ? 'Confirming...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminVerificationPage;